import { useState } from 'react';
import { Button, Chip, Stack } from '@mui/material';
import {
  CreateButton,
  Datagrid,
  FilterButton,
  FunctionField,
  List,
  SelectInput,
  TextInput,
  TopToolbar,
  useDataProvider,
  useGetMany,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
  useUnselectAll,
  type RaRecord,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { performStatusTransition } from '../actions/performStatusTransition';
import { TransitionButton } from '../actions/TransitionButton';
import { marketsForScope } from '../dataProvider/types';
import type { AdminDataProvider, RateRule } from '../dataProvider';
import {
  LIFECYCLE_COLOR,
  LIFECYCLE_LABEL,
  formatEffect,
  formatScope,
  ruleLifecycle,
} from './rateRuleState';

/**
 * Rate rules list (D-rate-rules, Phase 8 surface 2). The authored rules with
 * their scope, effect, market, priority and DERIVED lifecycle chip (off the live
 * clock). Per-row activate / pause and bulk activate / pause route through the
 * chokepoint, audited. OPERATIONAL market-scoped: the switcher governs which
 * rules show (the active markets plus the all-market rules). ROLE-gated to editor
 * and super_admin (a capability read-gate would leak via a viewer's '*:read').
 */

const LifecycleChip = () => {
  const record = useRecordContext<RateRule>();
  const [now] = useState<number>(() => Date.now());
  if (!record) return null;
  const state = ruleLifecycle(record, now);
  return (
    <Chip
      size="small"
      color={LIFECYCLE_COLOR[state]}
      label={LIFECYCLE_LABEL[state]}
      variant={state === 'live' ? 'filled' : 'outlined'}
      data-testid={`rule-lifecycle-${record.id}`}
    />
  );
};

/** Per-row activate / pause through the chokepoint (audited; no audit code here). */
const LifecycleActions = () => {
  const record = useRecordContext<RateRule>();
  const { role, accessibleRegions } = useAccessContext();
  if (!record) return null;
  const iso = new Date().toISOString();
  const canActivate = hasAccess(
    { role, accessibleRegions },
    { capability: 'rate_rules:activate' },
  );
  const canPause = hasAccess(
    { role, accessibleRegions },
    { capability: 'rate_rules:pause' },
  );

  return (
    <Stack direction="row" spacing={1}>
      {record.status !== 'active' && canActivate && (
        <TransitionButton
          resource="rateRules"
          capability="rate_rules:activate"
          id={record.id}
          data={{ status: 'active', updatedAt: iso }}
          previousData={{ id: record.id, status: record.status }}
          label="Activate"
          color="success"
          confirmTitle="Take this rule live"
          confirmContent="Activating makes the rule live for members (subject to its window). You can pause it again at any time."
          successMessage="Rule activated"
        />
      )}
      {record.status === 'active' && canPause && (
        <TransitionButton
          resource="rateRules"
          capability="rate_rules:pause"
          id={record.id}
          data={{ status: 'paused', updatedAt: iso }}
          previousData={{ id: record.id, status: 'active' }}
          label="Pause"
          color="warning"
          confirmTitle="Pause this rule"
          confirmContent="Pausing disables the rule for members. It can be activated again."
          successMessage="Rule paused"
        />
      )}
    </Stack>
  );
};

/** Bulk activate / pause: each affected rule is transitioned INDIVIDUALLY through
 *  the chokepoint, so each is guarded and audited as its own entry. */
const RateRuleBulkActions = () => {
  const { selectedIds = [] } = useListContext();
  const { role, accessibleRegions } = useAccessContext();
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll('rateRules');
  const { data: rules = [] } = useGetMany<RateRule>('rateRules', {
    ids: selectedIds,
  });
  const [busy, setBusy] = useState(false);

  const canActivate = hasAccess(
    { role, accessibleRegions },
    { capability: 'rate_rules:activate' },
  );
  const canPause = hasAccess(
    { role, accessibleRegions },
    { capability: 'rate_rules:pause' },
  );
  if (!canActivate && !canPause) return null;

  const run = async (
    capability: 'rate_rules:activate' | 'rate_rules:pause',
    toStatus: 'active' | 'paused',
    shouldTransition: (r: RateRule) => boolean,
  ) => {
    setBusy(true);
    const iso = new Date().toISOString();
    try {
      let n = 0;
      for (const rule of rules.filter(Boolean).filter(shouldTransition)) {
        await performStatusTransition({
          dataProvider,
          role,
          capability,
          resource: 'rateRules',
          id: rule.id,
          data: { status: toStatus, updatedAt: iso },
          previousData: { id: rule.id, status: rule.status },
        });
        n += 1;
      }
      notify(`${n} rule(s) ${toStatus === 'active' ? 'activated' : 'paused'}`, {
        type: 'success',
      });
      unselectAll();
      refresh();
    } catch {
      notify('Could not update some rules', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack direction="row" spacing={1}>
      {canActivate && (
        <Button
          size="small"
          disabled={busy}
          onClick={() =>
            run('rate_rules:activate', 'active', (r) => r.status !== 'active')
          }
          data-testid="bulk-activate"
        >
          Activate
        </Button>
      )}
      {canPause && (
        <Button
          size="small"
          disabled={busy}
          onClick={() =>
            run('rate_rules:pause', 'paused', (r) => r.status === 'active')
          }
          data-testid="bulk-pause"
        >
          Pause
        </Button>
      )}
    </Stack>
  );
};

const ruleFilters = [
  <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: 'draft', name: 'Draft' },
      { id: 'active', name: 'Active' },
      { id: 'paused', name: 'Paused' },
    ]}
  />,
  <SelectInput
    key="scopeType"
    source="scopeType"
    label="Scope"
    choices={[
      { id: 'store', name: 'Store' },
      { id: 'marketplace', name: 'Marketplace' },
      { id: 'category', name: 'Category' },
      { id: 'marketplace_category', name: 'Marketplace in category' },
      { id: 'tag', name: 'Tag' },
      { id: 'tag_marketplaces', name: 'Tag on marketplaces' },
      { id: 'store_list', name: 'Store list' },
      { id: 'market', name: 'Market' },
    ]}
  />,
];

const RateRuleListActions = () => {
  const { role, accessibleRegions } = useAccessContext();
  const canAuthor = hasAccess(
    { role, accessibleRegions },
    { capability: 'rate_rules:author' },
  );
  return (
    <TopToolbar>
      <FilterButton />
      {canAuthor && <CreateButton />}
    </TopToolbar>
  );
};

/** The list itself. Exported for testing without the access wrapper. */
export const RateRuleListView = () => {
  const { activeRegion } = useRegion();
  // Operational scoping: the active markets PLUS the all-market rules.
  const markets = [...marketsForScope(activeRegion ?? 'GLOBAL'), 'all'];
  return (
    <List
      resource="rateRules"
      title="Rate rules"
      filter={{ market: markets }}
      filters={ruleFilters}
      actions={<RateRuleListActions />}
      sort={{ field: 'priority', order: 'DESC' }}
      perPage={25}
      exporter={false}
    >
      <Datagrid rowClick="edit" bulkActionButtons={<RateRuleBulkActions />}>
        <FunctionField
          label="Label"
          render={(r: RateRule) => r.displayLabel ?? '(unlabelled)'}
        />
        <FunctionField
          label="Scope"
          render={(r: RaRecord) => formatScope(r as RateRule)}
        />
        <FunctionField
          label="Effect"
          render={(r: RaRecord) => formatEffect(r as RateRule)}
        />
        <FunctionField
          label="Market"
          render={(r: RateRule) => String(r.market).toUpperCase()}
        />
        <FunctionField
          label="Priority"
          sortBy="priority"
          render={(r: RateRule) => r.priority}
        />
        <FunctionField label="State" render={() => <LifecycleChip />} />
        <FunctionField label="Actions" render={() => <LifecycleActions />} />
      </Datagrid>
    </List>
  );
};

/** Gated entry point. ROLE-gated to editor and super_admin (a capability read-gate
 *  would leak via a viewer's '*:read'). */
export const RateRuleList = () => (
  <RequireAccess roles={['editor', 'super_admin']}>
    <RateRuleListView />
  </RequireAccess>
);
